import React from 'react'
import { Button, Card, CardContent, CardHeader, Input, Label, Separator } from '@/shared/ui'
import { getBranding, setLogoConfig } from '@/modules/branding/brandingService'
import { uploadToBucket } from '@/modules/upload/services/uploadService'
import LogoUploadField from '@/modules/branding/components/LogoUploadField'

type SectionState = {
  light: string | null
  dark: string | null
  saving: boolean
}

type BrandingSection = 'sidebar_icon' | 'sidebar_logo' | 'home' | 'login' | 'pwa_icon'
type BrandingVariant = 'light' | 'dark'

const sections: Array<{ key: BrandingSection; title: string; description: string }> = [
  { key: 'sidebar_icon', title: 'Ícone do Menu Lateral', description: 'Pequeno ícone exibido sempre; visível também quando o menu está recolhido.' },
  { key: 'sidebar_logo', title: 'Logo (wordmark) do Menu Lateral', description: 'Imagem do nome/marca exibida ao lado do ícone quando o menu está expandido.' },
  { key: 'home', title: 'Logo do Header (Home)', description: 'Aparece no cabeçalho da página inicial.' },
  { key: 'login', title: 'Logo da Página de Login', description: 'Aparece acima do texto "Bem-vindo de volta".' },
  { key: 'pwa_icon', title: 'Ícone do App (PWA)', description: 'Ícone usado quando o usuário instala o app no celular. Recomenda-se PNG 512x512 com fundo transparente.' },
]

export default function BrandingManager() {
  const [state, setState] = React.useState<Record<BrandingSection, SectionState>>({
    sidebar_icon: { light: null, dark: null, saving: false },
    sidebar_logo: { light: null, dark: null, saving: false },
    home: { light: null, dark: null, saving: false },
    login: { light: null, dark: null, saving: false },
    pwa_icon: { light: null, dark: null, saving: false },
  })
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const b = await getBranding()
        if (!mounted) return
        setState((s) => ({
          sidebar_icon: { light: b?.sidebar_icon?.light || null, dark: b?.sidebar_icon?.dark || null, saving: false },
          sidebar_logo: { light: b?.sidebar_logo?.light || null, dark: b?.sidebar_logo?.dark || null, saving: false },
          home: { light: b?.home?.light || null, dark: b?.home?.dark || null, saving: false },
          login: { light: b?.login?.light || null, dark: b?.login?.dark || null, saving: false },
          pwa_icon: { light: b?.pwa_icon?.light || null, dark: b?.pwa_icon?.dark || null, saving: false },
        }))
      } catch (e: any) {
        setError(e?.message || 'Falha ao carregar')
      } finally {
        setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  async function onUpload(section: BrandingSection, variant: BrandingVariant, file: File) {
    setError(null)
    setState((s) => ({ ...s, [section]: { ...s[section], saving: true } }))
    try {
      const { url } = await uploadToBucket('branding', file, `${section}/${variant}`)
      const current = state[section]
      const nextLight = variant === 'light' ? url : current.light
      const nextDark = variant === 'dark' ? url : current.dark
      await setLogoConfig(section, nextLight ?? null, nextDark ?? null)
      setState((s) => ({ ...s, [section]: { ...s[section], light: nextLight ?? null, dark: nextDark ?? null } }))
    } catch (e: any) {
      setError(e?.message || 'Falha no upload')
    } finally {
      setState((s) => ({ ...s, [section]: { ...s[section], saving: false } }))
    }
  }

  if (loading) return <div className="text-sm text-foreground/70">Carregando…</div>

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
      )}
      {sections.map(({ key, title, description }) => {
        const sec = state[key]
        return (
          <Card key={key}>
            <CardHeader className="text-sm font-medium">
              {title}
              <div className="mt-1 text-xs text-foreground/70">{description}</div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <LogoUploadField
                  label="Versão Light"
                  url={sec.light}
                  disabled={sec.saving}
                  onSelect={(f) => onUpload(key, 'light', f)}
                />
                <LogoUploadField
                  label="Versão Dark"
                  url={sec.dark}
                  disabled={sec.saving}
                  onSelect={(f) => onUpload(key, 'dark', f)}
                />
              </div>
              <Separator />
              <div className="flex gap-2 text-xs text-foreground/70">
                <span>Dica:</span>
                <span>Imagens PNG com fundo transparente funcionam melhor. Tamanho sugerido ~24px de altura.</span>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// UploadField foi extraído para um componente reutilizável (LogoUploadField)
